import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAnalytics } from "../lib/analytics";
import { analyzeAuthFlow } from "../lib/auth-flow";
import { useClientTranslations } from "../lib/i18n/client";

export function Callback() {
	const { t } = useClientTranslations("common");
	const {
		error,
		isLoading,
		isAuthenticated,
		handleRedirectCallback,
		user,
		getAccessTokenSilently,
	} = useAuth0();
	const navigate = useNavigate();
	const hasProcessedFlow = useRef(false);
	const analytics = useAnalytics();

	// Track page view
	useEffect(() => {
		analytics.logEvent("page_view", "callback");
	}, [analytics]);

	// Process the Auth0 callback (authorization code exchange)
	useEffect(() => {
		const processCallback = async () => {
			// Only process if we haven't completed the Auth0 flow yet
			if (!isLoading && !isAuthenticated && !error) {
				console.log("📞 Callback: Processing Auth0 authorization code...");
				analytics.logUserAction("callback_processing_started");
				try {
					// This processes the code from URL params and completes the Auth0 flow
					await handleRedirectCallback();
					console.log("📞 Callback: Auth0 code processing complete");
					analytics.logUserAction("callback_processing_success");
					// After this, isAuthenticated will be true and SessionGuard can take over
				} catch (err) {
					console.error("📞 Callback: Auth0 code processing failed:", err);
					const errorMessage =
						err instanceof Error ? err.message : "Callback processing failed";
					analytics.logError(errorMessage, {
						context: "callback_code_processing",
					});
				}
			}
		};

		processCallback();
	}, [isLoading, isAuthenticated, error, handleRedirectCallback, analytics]);

	// After Auth0 completes, analyze flow and redirect
	useEffect(() => {
		if (!isLoading && isAuthenticated && user && !hasProcessedFlow.current) {
			hasProcessedFlow.current = true;

			const handlePostAuth = async () => {
				console.log("📞 Callback: Auth complete, analyzing flow...");
				const authFlowResult = analyzeAuthFlow(user, "/callback");

				analytics.logUserAction("callback_flow_analyzed", {
					reason: authFlowResult.reason,
					should_redirect: String(authFlowResult.shouldRedirect),
					redirect_path: authFlowResult.redirectPath || "none",
				});

				// Handle missing claims (post-registration on mobile)
				// JWT doesn't have custom claims because post-login action wasn't triggered
				if (authFlowResult.reason === "claims_missing") {
					console.log(
						"📞 Callback: Claims missing (post-registration), fetching status from API...",
					);
					analytics.logUserAction("callback_fetching_status", {
						reason: "claims_missing",
					});
					try {
						const token = await getAccessTokenSilently({
							authorizationParams: {
								audience: import.meta.env.VITE_AUTH0_AUDIENCE,
							},
						});
						const response = await fetch(
							`${import.meta.env.VITE_API_URL}/api/user/status`,
							{
								method: "GET",
								headers: {
									Authorization: `Bearer ${token}`,
									"Content-Type": "application/json",
								},
							},
						);

						if (response.ok) {
							const status = await response.json();
							console.log("📞 Callback: Got status from API:", status);

							analytics.logUserAction("callback_status_fetched", {
								needs_2fa: String(status.needs_2fa),
								needs_onboarding: String(status.needs_onboarding),
							});

							// Note: user_signed_up / user_logged_in events are now logged from the API

							// Navigate based on API response
							if (status.needs_2fa) {
								console.log("📞 Callback: User needs 2FA, redirecting...");
								analytics.logUserAction("callback_redirect_2fa");
								navigate(`/2fa?callbackUrl=${encodeURIComponent("/")}`, {
									replace: true,
								});
							} else if (status.needs_onboarding) {
								console.log(
									"📞 Callback: User needs onboarding, redirecting...",
								);
								analytics.logUserAction("callback_redirect_onboarding");
								navigate("/onboarding", { replace: true });
							} else {
								console.log("📞 Callback: User is all set, going to /");
								analytics.logUserAction("callback_redirect_chat");
								navigate("/", { replace: true });
							}
						} else {
							console.error(
								"📞 Callback: Failed to fetch status, defaulting to /",
							);
							analytics.logError("Status fetch failed", {
								context: "callback_status_fetch",
								status: String(response.status),
							});
							navigate("/", { replace: true });
						}
					} catch (err) {
						console.error(
							"📞 Callback: Error fetching status, defaulting to /:",
							err,
						);
						const errorMessage =
							err instanceof Error ? err.message : "Status fetch error";
						analytics.logError(errorMessage, {
							context: "callback_status_fetch",
						});
						navigate("/", { replace: true });
					}
					return;
				}

				// Normal flow (claims present in JWT)
				// Note: user_signed_up / user_logged_in events are now logged from the API

				if (authFlowResult.shouldRedirect && authFlowResult.redirectPath) {
					console.log(
						`📞 Callback: Redirecting to ${authFlowResult.redirectPath} (${authFlowResult.reason})`,
					);
					analytics.logUserAction("callback_redirect", {
						path: authFlowResult.redirectPath,
						reason: authFlowResult.reason,
					});
					navigate(authFlowResult.redirectPath, { replace: true });
				} else {
					console.log("📞 Callback: No redirect needed, going to /");
					analytics.logUserAction("callback_redirect_default");
					navigate("/", { replace: true });
				}
			};

			handlePostAuth();
		}
	}, [
		isLoading,
		isAuthenticated,
		user,
		navigate,
		getAccessTokenSilently,
		analytics,
	]);

	// Handle authentication errors
	useEffect(() => {
		if (error) {
			analytics.logError(error.message, { context: "callback_auth_error" });
		}
	}, [error, analytics]);

	if (error) {
		return (
			<div style={{ padding: "2rem", textAlign: "center" }}>
				<h2>{t("auth.error")}</h2>
				<p style={{ color: "red" }}>{error.message}</p>
				<button type="button" onClick={() => navigate("/login")}>
					{t("buttons.backToLogin")}
				</button>
			</div>
		);
	}

	// Show loading while Auth0 processes the authorization code
	// Once complete, SessionGuard will handle JWT claims analysis and redirect
	return (
		<div
			style={{
				display: "flex",
				justifyContent: "center",
				alignItems: "center",
				height: "100vh",
			}}
		>
			<div style={{ textAlign: "center" }}>
				<h2>{t("auth.completing")}</h2>
				<p>{t("auth.processingLogin")}</p>
			</div>
		</div>
	);
}
